import { ReactElement, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@twilio-paste/core/button";
import { Modal } from "@twilio-paste/core/modal";
import {
  ModalHeader,
  ModalHeading,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
} from "@twilio-paste/core/modal";
import { Grid, Column } from "@twilio-paste/core/grid";
import { Box } from "@twilio-paste/core/box";
import { Paragraph } from "@twilio-paste/core/paragraph";
import { Label } from "@twilio-paste/core/label";
import { Select, Option } from "@twilio-paste/core/select";
import { Text } from "@twilio-paste/core/text";
import { TextArea } from "@twilio-paste/core/textarea";
import {
  Callout,
  CalloutHeading,
  CalloutText,
} from "@twilio-paste/core/callout";
import {
  wiseOwlViewCopy,
  WiseOwlIngress,
} from "@twiliointernal/e2-commons-shared-definitions";

import { copy } from "./definitions";
import {
  LIVE_CHAT_SUPPORTED_PRODUCTS,
  MESSAGING_INSIGHTS_PRODUCT_CATEGORY,
  UNSUPPORTED_PRODUCT,
  SGHC_LIVE_CHAT_SUPPORTED_PRODUCTS,
  mapInferredProductToSupportedProduct,
  ATO_INSIGHT_PRODUCT_CATEGORY,
} from "./definitions/constants";
import {
  LiveChatPreEngagementProps,
  LiveChatPreEngagementFormValues,
  LiveChatPreEngagementFormFields,
} from "./definitions/types";

const LiveChatPreEngagement = (
  props: LiveChatPreEngagementProps
): ReactElement => {
  const {
    isOpen = false,
    handleStartEngagement,
    handleToggleVisibility,
    selectedProductCategory,
    message,
    ingress,
  } = props;

  const isSendGridIngress = ingress === WiseOwlIngress.sendGrid;

  // Pre-populated productCategory and message as these values are updated from parent
  // and when the modal open state updates
  useEffect(() => {
    if (selectedProductCategory) {
      setValue(
        "product",
        mapInferredProductToSupportedProduct(selectedProductCategory)
      );
    }

    if (message) {
      setValue("message", message);
    }
  }, [selectedProductCategory, message, isOpen]);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
    watch,
    reset,
  } = useForm<LiveChatPreEngagementFormValues>({
    defaultValues: {
      product: "",
      message: "",
    },
  });

  const shouldShowProductDropdown = !(
    selectedProductCategory === MESSAGING_INSIGHTS_PRODUCT_CATEGORY ||
    selectedProductCategory === ATO_INSIGHT_PRODUCT_CATEGORY
  );

  watch(LiveChatPreEngagementFormFields.Product);

  watch(LiveChatPreEngagementFormFields.Message);

  const handleModalClose = (): void => {
    reset();
    handleToggleVisibility();
  };

  const handleStartLiveChat = (data: LiveChatPreEngagementFormValues): void => {
    const { product, message } = data;

    handleStartEngagement(product, message);
    handleModalClose();
  };

  const modalHeadingID = "live-chat-pre-engagement-modal";

  const renderSelectedProductAction = (): ReactElement | null => {
    const selectedProduct = getValues("product");

    if (!selectedProduct) {
      return null;
    }

    if (selectedProduct === UNSUPPORTED_PRODUCT) {
      const submitLink = "/submit";

      return (
        <Box>
          <Callout variant="neutral">
            <Box>
              <CalloutHeading as="h2">
                {copy.liveChatPreEngagement.unsupportedProductCallout.heading}
              </CalloutHeading>
              <CalloutText>
                {copy.liveChatPreEngagement.unsupportedProductCallout.description(
                  submitLink
                )}
              </CalloutText>
            </Box>
          </Callout>
        </Box>
      );
    }

    return (
      <Box marginBottom="space10">
        <Label required htmlFor={LiveChatPreEngagementFormFields.Message}>
          {copy.liveChatPreEngagement.message.label}
        </Label>
        <Controller
          name={LiveChatPreEngagementFormFields.Message}
          control={control}
          rules={{
            required: true,
          }}
          render={({ field }): JSX.Element => {
            return (
              <TextArea
                id={LiveChatPreEngagementFormFields.Message}
                required
                {...field}
              />
            );
          }}
        />
        {/* Live Chat Disclaimer */}
        <Box marginTop="space40">
          <Text as="summary" color="colorTextWeak" fontSize="fontSize20">
            {wiseOwlViewCopy.liveChatDisclaimer}
          </Text>
        </Box>
      </Box>
    );
  };

  const productFieldError = errors[LiveChatPreEngagementFormFields.Product];

  const messageFieldError = errors[LiveChatPreEngagementFormFields.Message];

  const hasFieldError = !!productFieldError || !!messageFieldError;

  const invalidProduct =
    !getValues("product") || getValues("product") === UNSUPPORTED_PRODUCT;

  const invalidMessage = !getValues("message").trim().length;

  const isLiveChatDisabled = invalidProduct || invalidMessage || hasFieldError;

  return (
    <Modal
      ariaLabelledby={modalHeadingID}
      isOpen={isOpen}
      onDismiss={handleModalClose}
      size="default"
    >
      <ModalHeader>
        <ModalHeading as="h3" id={modalHeadingID}>
          {copy.liveChatPreEngagement.heading}
        </ModalHeading>
      </ModalHeader>
      <ModalBody>
        <Grid>
          <Column as="form">
            {shouldShowProductDropdown && (
              <>
                {!isSendGridIngress && (
                  <Box>
                    <Paragraph>
                      {copy.liveChatPreEngagement.description}
                    </Paragraph>
                  </Box>
                )}

                <Box marginBottom="space50">
                  <Label
                    required
                    htmlFor={LiveChatPreEngagementFormFields.Product}
                  >
                    {copy.liveChatPreEngagement.supportedProducts.label}
                  </Label>

                  <Controller
                    name={LiveChatPreEngagementFormFields.Product}
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field }): JSX.Element => {
                      return (
                        <Select
                          id={LiveChatPreEngagementFormFields.Product}
                          required
                          {...field}
                        >
                          {!getValues(
                            LiveChatPreEngagementFormFields.Product
                          ) && <Option value=""> </Option>}
                          {isSendGridIngress &&
                            SGHC_LIVE_CHAT_SUPPORTED_PRODUCTS.map((product) => (
                              <Option key={product.value} value={product.value}>
                                {product.label}
                              </Option>
                            ))}
                          {!isSendGridIngress &&
                            LIVE_CHAT_SUPPORTED_PRODUCTS.map((product) => (
                              <Option key={product.value} value={product.value}>
                                {product.label}
                              </Option>
                            ))}
                        </Select>
                      );
                    }}
                  />
                </Box>
              </>
            )}

            {renderSelectedProductAction()}
          </Column>
        </Grid>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <Button variant="secondary" onClick={handleModalClose}>
            {copy.liveChatPreEngagement.closeFormButton}
          </Button>
          <Button
            onClick={handleSubmit(handleStartLiveChat)}
            variant="primary"
            disabled={isLiveChatDisabled}
          >
            {copy.liveChatPreEngagement.startLiveChatButton}
          </Button>
        </ModalFooterActions>
      </ModalFooter>
    </Modal>
  );
};

export default LiveChatPreEngagement;
